import React from 'react';
import { FileText, Send, Upload, Home as HomeIcon, Trophy, CalendarRange } from 'lucide-react';
import { motion } from 'framer-motion';
import { MetricTile } from './MetricTile';
import { Clock, Phone, PhoneCall } from 'lucide-react';
import { Loan, LoanStage, STAGE_POINTS } from '../data/loans';
import { OfficerData } from '../data/officers';
interface CustomRangeScorecardProps {
  startDate: string; // ISO yyyy-mm-dd
  endDate: string;
  loans: Loan[];
  officer: OfficerData;
  onTileClick?: (stage: LoanStage) => void;
  onSpeedBonusClick?: () => void;
  onAnswerRateClick?: () => void;
  onDialsClick?: () => void;
}
function formatDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function daysBetween(a: string, b: string) {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const start = new Date(ay, am - 1, ad).getTime();
  const end = new Date(by, bm - 1, bd).getTime();
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
}
export function CustomRangeScorecard({
  startDate,
  endDate,
  loans,
  officer,
  onTileClick,
  onSpeedBonusClick,
  onAnswerRateClick,
  onDialsClick
}: CustomRangeScorecardProps) {
  const LOANS = loans;
  // Aggregate events that fall inside [startDate, endDate]
  const inRange = (d: string) => d >= startDate && d <= endDate;
  const stageStats: Record<LoanStage, {
    count: number;
    points: number;
  }> = {
    credit_pull: {
      count: 0,
      points: 0
    },
    doc_out: {
      count: 0,
      points: 0
    },
    submission: {
      count: 0,
      points: 0
    },
    closing: {
      count: 0,
      points: 0
    }
  };
  LOANS.forEach(loan => {
    loan.events.forEach(e => {
      if (inRange(e.date)) {
        stageStats[e.stage].count += 1;
        stageStats[e.stage].points += e.points;
      }
    });
  });
  const totalPoints = Object.values(stageStats).reduce((s, v) => s + v.points, 0);
  const totalActions = Object.values(stageStats).reduce((s, v) => s + v.count, 0);
  const days = daysBetween(startDate, endDate);
  const dailyAvg = Math.round(totalPoints / days);
  // Speed bonus loans whose closing date falls within the range
  const speedBonusLoans = LOANS.filter(loan => {
    if (!loan.speedBonus || loan.speedBonus <= 0) return false;
    const closing = loan.events.find(e => e.stage === 'closing');
    return closing ? inRange(closing.date) : false;
  });
  const speedBonusPts = speedBonusLoans.reduce((s, l) => s + (l.speedBonus ?? 0), 0);
  const invalid = startDate > endDate;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" data-id="element-190">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between gap-3 text-white" data-id="element-191">
        <div className="flex items-center gap-2 min-w-0" data-id="element-192">
          <CalendarRange className="w-5 h-5 shrink-0" data-id="element-193" />
          <h2 className="font-bold tracking-wide truncate" data-id="element-194">CUSTOM RANGE</h2>
        </div>
        <span className="text-xs sm:text-sm font-medium text-blue-200 shrink-0" data-id="element-195">
          {formatDate(startDate)} – {formatDate(endDate)}
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-196">
        {invalid ? <div className="text-center py-10 text-gray-500" data-id="element-197">
            <p className="font-bold text-red-600 mb-1" data-id="element-198">Invalid date range</p>
            <p className="text-sm" data-id="element-199">
              Start date must be on or before the end date.
            </p>
          </div> : <>
            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-gray-200 gap-y-4 mb-6" data-id="element-200">
              <SummaryStat label="Total Points" sub="In Range" value={totalPoints.toLocaleString()} valueClass="text-[#0a1e5e]" data-id="element-201" />
              <SummaryStat label="Actions" sub="Logged" value={totalActions.toString()} valueClass="text-[#0a1e5e]" data-id="element-202" />
              <SummaryStat label="Days" sub="In Range" value={days.toString()} valueClass="text-[#0a1e5e]" data-id="element-203" />
              <SummaryStat label="Daily Avg" sub="Pts / Day" value={dailyAvg.toLocaleString()} valueClass="text-green-600" data-id="element-204" />
            </div>

            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-205">
              Range Breakdown
            </h3>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-id="element-206">
              <MetricTile title="Call Answer Rate" points={0} count={officer.callAnswerRate} countSuffix="%" countLabel="Answer Rate" icon={Phone} colorClass="text-teal-600" bgClass="bg-teal-100" hidePoints onClick={onAnswerRateClick} data-id="element-207" />
              <MetricTile title="Dials" points={0} count={officer.dials} countLabel="Dials" icon={PhoneCall} colorClass="text-indigo-600" bgClass="bg-indigo-100" hidePoints onClick={onDialsClick} data-id="element-208" />
              <MetricTile title="Credit Pulls" points={STAGE_POINTS.credit_pull} count={stageStats.credit_pull.count} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" ptsPerAction={STAGE_POINTS.credit_pull} onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-209" />
              <MetricTile title="Doc Outs" points={STAGE_POINTS.doc_out} count={stageStats.doc_out.count} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" ptsPerAction={STAGE_POINTS.doc_out} onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-210" />
              <MetricTile title="Submissions" points={STAGE_POINTS.submission} count={stageStats.submission.count} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" ptsPerAction={STAGE_POINTS.submission} onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-211" />
              <MetricTile title="Closings" points={STAGE_POINTS.closing} count={stageStats.closing.count} countLabel="Closings" icon={HomeIcon} colorClass="text-orange-500" bgClass="bg-orange-100" ptsPerAction={STAGE_POINTS.closing} onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-212" />
            </div>

            {/* Speed Bonus in range */}
            {speedBonusLoans.length > 0 && <button type="button" onClick={onSpeedBonusClick} disabled={!onSpeedBonusClick} aria-label="View speed bonus loans in range" className={`group relative w-full bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3 mb-4 transition-all text-left ${onSpeedBonusClick ? 'hover:border-orange-400 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400' : 'cursor-default'}`} data-id="element-213">
                <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-214">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-215">
                    <Clock className="w-6 h-6 text-white" data-id="element-216" />
                  </div>
                  <div className="min-w-0" data-id="element-217">
                    <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-218">
                      Speed Bonus in Range
                    </p>
                    <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-219">
                      Close a loan within 14 days of credit pull
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0" data-id="element-220">
                  <div className="flex items-baseline gap-1 justify-end" data-id="element-221">
                    <span className="text-2xl font-extrabold text-orange-500 leading-none" data-id="element-222">
                      +{speedBonusPts.toLocaleString()}
                    </span>
                    <span className="text-sm font-bold text-gray-500" data-id="element-223">PTS</span>
                  </div>
                  <p className="text-[10px] font-bold text-[#0a1e5e] uppercase mt-1" data-id="element-224">
                    {speedBonusLoans.length} Fast{' '}
                    {speedBonusLoans.length === 1 ? 'Close' : 'Closes'}
                  </p>
                </div>
                {onSpeedBonusClick && <span className="absolute top-2 right-2 text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider" data-id="element-225">
                    View loans →
                  </span>}
              </button>}

            {/* Total banner */}
            <div className="mt-auto bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-id="element-226">
              <div className="w-12 h-12 bg-[#0a1e5e] rounded-full flex items-center justify-center shrink-0" data-id="element-227">
                <Trophy className="w-6 h-6 text-yellow-400" data-id="element-228" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-229">
                <p className="text-xs font-bold text-gray-500 uppercase leading-tight" data-id="element-230">
                  Total Points in Range
                </p>
                <div className="flex items-baseline gap-1 flex-wrap" data-id="element-231">
                  <span className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-232">
                    {totalPoints.toLocaleString()}
                  </span>
                  <span className="text-xs font-bold text-gray-500" data-id="element-233">
                    POINTS
                  </span>
                </div>
              </div>
              {totalActions === 0 && <span className="text-xs font-semibold text-gray-400 shrink-0" data-id="element-234">
                  No activity in this range
                </span>}
            </div>
          </>}
      </div>
    </motion.div>;
}
function SummaryStat({
  label,
  sub,
  value,
  valueClass
}: {
  label: string;
  sub: string;
  value: string;
  valueClass: string;
}) {
  return <div className="text-center px-2 min-w-0" data-id="element-235">
      <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-236">
        {label}
      </p>
      <p className={`text-2xl sm:text-3xl font-extrabold leading-none ${valueClass}`} data-id="element-237">
        {value}
      </p>
      <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-238">
        {sub}
      </p>
    </div>;
}